import React from 'react';
import cn from 'classnames';
import { Map } from 'immutable';
import styles from './ResourcePermissions.module.scss';
import { DropdownMenuOption, ID } from '@app/types';
import DropdownMenu from '@components/Menus/DropdownMenu';
import { Toggle } from '@components/FormInputs';
import { ChevronDownIcon } from '@components/Icons';
import { itemListToString } from '@support/string';
import {
  labelsForPermissionsByResourceType,
  supportedPermissionsByResourceType,
  SharedResourceType,
} from '@reducers/data/shareStatuses';

type PermissionsMap<PermissionType> = Map<PermissionType, ID | null | true>;

function PermissionOption<P extends string>({
  sharedResourceType,
  permission,
  checked,
  onChange,
  disabled,
}: React.InputHTMLAttributes<HTMLInputElement> & {
  sharedResourceType: SharedResourceType;
  permission: P;
}) {
  const labelForPermission = labelsForPermissionsByResourceType[sharedResourceType] as {
    [permission in P]: string;
  };
  return (
    <Toggle
      className={cn(styles.option, checked && styles.checked, disabled && styles.disabled)}
      name={permission}
      checked={checked}
      onChange={onChange}
      clickableLabel={`Allow access to ${labelForPermission[permission]}`}
      disabled={disabled}
    />
  );
}

export function PermissionsEditorTriggerButton<P extends string>({
  isOpen,
  triggerProps,
}: {
  isOpen: boolean;
  triggerProps: { sharedResourceType: SharedResourceType; permissions: PermissionsMap<P> };
}) {
  const supportedPermissions = supportedPermissionsByResourceType[
    triggerProps.sharedResourceType
  ] as P[];
  const labelForPermission = labelsForPermissionsByResourceType[
    triggerProps.sharedResourceType
  ] as { [permission in P]: string };
  const maxPermissionsCount = supportedPermissions.length;
  const enabledPermissions = supportedPermissions.filter((permission) =>
    triggerProps.permissions?.has(permission),
  );
  const enabledPermissionsCount = enabledPermissions.length || 0;
  let label = 'Public Info only';

  if (enabledPermissionsCount > 0 && enabledPermissionsCount < maxPermissionsCount) {
    label = itemListToString(
      enabledPermissions.map((permission) => labelForPermission[permission]),
    );
  } else if (enabledPermissionsCount == maxPermissionsCount) {
    label = 'Full profile';
  }

  return (
    <span className={cn(styles.editorTriggerButton, isOpen && styles.editorMenuOpen)}>
      {label}
      <ChevronDownIcon color="textSecondary" className={styles.triggerIcon} />
    </span>
  );
}

export default function ResourcePermissionsEditor<P>({
  sharedResourceType,
  globalPermissions = false,
  permissions,
  addPermission,
  removePermission,
  unshare,
  disabled,
}: {
  sharedResourceType: SharedResourceType;
  globalPermissions?: boolean;
  permissions: PermissionsMap<P>;
  addPermission: (permission: P) => void;
  removePermission: (permission: P) => void;
  unshare?: () => void;
  disabled?: boolean;
}) {
  const handleToggleClickFor = (permissionType: P) => () => {
    if (permissions.has(permissionType)) {
      removePermission(permissionType);
    } else {
      addPermission(permissionType);
    }
  };

  const supportedPermissions = supportedPermissionsByResourceType[sharedResourceType] as P[];

  let options: DropdownMenuOption[] = supportedPermissions.map((permission: P) => {
    const optionDisabled =
      !globalPermissions && permissions.has(permission) && !permissions.get(permission);
    return {
      title: (
        <PermissionOption
          sharedResourceType={sharedResourceType}
          permission={permission as string}
          checked={permissions.has(permission)}
          onChange={handleToggleClickFor(permission)}
          disabled={optionDisabled}
        />
      ),
      action: () => {},
      keepMenuOpenOnClick: true,
      className: cn(styles.accessOption, disabled && styles.disabled),
    };
  });

  if (!globalPermissions && unshare) {
    options = options.concat([
      {
        divider: true,
        disabled: true,
      },
      {
        title: 'Remove access',
        action: unshare,
      },
    ]);
  }

  return (
    <DropdownMenu
      triggerVariant="permissions"
      triggerProps={{ sharedResourceType, permissions }}
      expandFrom="top-right"
      options={options}
      triggerClassName={styles.editorTrigger}
      disabled={disabled}
    />
  );
}
